import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { 
  Shield, 
  ArrowRight, 
  Stethoscope,
  ShieldCheck,
  Lock,
  FileCheck,
  KeyRound,
  Brain,
  Clock,
  Users,
  Activity,
  HeartPulse,
  Building2,
  Quote,
  HelpCircle,
  Play
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { HreflangTags } from "@/components/HreflangTags";
import PlatformDemoModal from "@/components/demo/PlatformDemoModal";

export default function HomePage() {
  const [showDemo, setShowDemo] = useState(false);

  return (
    <>
      <Helmet>
        <title>CheckFirstCare by Ranvir Gill - Patient Triage Platform | Healthcare Triage Software</title>
        <meta name="description" content="CheckFirstCare, founded by Ranvir Gill, is an AI-powered patient triage platform and emergency room triage app. Hospital triage automation with clinical decision support tool. Virtual healthcare triage system for better patient outcomes." />
        <meta name="keywords" content="Ranvir Gill, Ranvir Gill healthcare, Ranvir Gill founder, CheckFirstCare founder, patient triage platform, healthcare triage software, AI symptom checker for hospitals, emergency room triage app, virtual healthcare triage system, hospital triage automation, clinical decision support tool, digital front door for hospitals" />
        <meta name="author" content="Ranvir Gill" />
        
        <meta property="og:title" content="CheckFirstCare by Ranvir Gill - Patient Triage Platform | Healthcare Triage Software" />
        <meta property="og:description" content="Founded by Ranvir Gill. AI-powered patient triage platform and emergency room triage app. Hospital triage automation with clinical decision support tool." />
        <meta property="og:url" content="https://www.checkfirstcare.com/" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://www.checkfirstcare.com/og-image.jpg" />
        
        <meta name="twitter:title" content="CheckFirstCare by Ranvir Gill - Patient Triage Platform | Healthcare Triage Software" />
        <meta name="twitter:description" content="Founded by Ranvir Gill. AI-powered patient triage platform and emergency room triage app. Hospital triage automation with clinical decision support tool." />
        <meta name="twitter:image" content="https://www.checkfirstcare.com/og-image.jpg" />
        
        <link rel="canonical" href="https://www.checkfirstcare.com/" />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebSite",
            "name": "CheckFirstCare",
            "url": "https://www.checkfirstcare.com",
            "description": "AI-powered patient triage platform founded by Ranvir Gill",
            "creator": {
              "@type": "Person",
              "name": "Ranvir Gill",
              "jobTitle": "Founder & CEO",
              "url": "https://www.checkfirstcare.com/about",
              "image": "https://www.checkfirstcare.com/ranvir-gill.jpg"
            },
            "publisher": {
              "@type": "Organization",
              "name": "CheckFirstCare",
              "founder": {
                "@type": "Person",
                "name": "Ranvir Gill"
              }
            },
            "potentialAction": {
              "@type": "SearchAction",
              "target": "https://www.checkfirstcare.com/blog?q={search_term_string}",
              "query-input": "required name=search_term_string"
            }
          })}
        </script>

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "SiteNavigationElement",
            "name": ["Home", "Triage", "About", "Blog", "Contact"],
            "url": [
              "https://www.checkfirstcare.com/",
              "https://www.checkfirstcare.com/triage",
              "https://www.checkfirstcare.com/about",
              "https://www.checkfirstcare.com/blog",
              "https://www.checkfirstcare.com/contact"
            ]
          })}
        </script>

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "What is CheckFirstCare and how does it work?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "CheckFirstCare is an AI-powered patient triage platform that helps you make informed healthcare decisions. Simply answer questions about your symptoms, and our intelligent system analyzes your responses to recommend the most appropriate level of care—whether that's staying home with self-care advice, scheduling a doctor's visit, going to urgent care, or heading to the emergency room."
                }
              },
              {
                "@type": "Question",
                "name": "Is my health information secure and private?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Absolutely. CheckFirstCare is fully HIPAA compliant and uses enterprise-grade security measures including AES-256 encryption, SOC 2 Type II certification, and multi-factor authentication. Your health data is encrypted both in transit and at rest, and we never sell or share your personal information with third parties."
                }
              },
              {
                "@type": "Question",
                "name": "Does CheckFirstCare replace medical advice from a doctor?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "No, CheckFirstCare is designed to be a decision support tool, not a replacement for professional medical advice. Our platform helps you determine the urgency of your symptoms and guides you to the appropriate level of care. For any medical emergency, always call 911 or go directly to the emergency room."
                }
              },
              {
                "@type": "Question",
                "name": "How accurate is the AI triage system?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Our AI triage system is built on evidence-based clinical protocols and continuously refined with input from healthcare professionals. The system is designed to err on the side of caution—when in doubt, it recommends seeking higher levels of care. Our algorithms have been validated against established triage standards used in emergency departments."
                }
              },
              {
                "@type": "Question",
                "name": "What do I need to get started with CheckFirstCare?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "To use CheckFirstCare, you'll need an agency code from your healthcare provider. This connects you to your healthcare organization's account and ensures your triage results are properly documented. Simply enter your email, verify with the one-time code, and input your agency code to begin your assessment."
                }
              },
              {
                "@type": "Question",
                "name": "How can my healthcare organization partner with CheckFirstCare?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Healthcare organizations can integrate CheckFirstCare to streamline patient intake, reduce unnecessary ER visits, and improve care coordination. We offer customizable solutions with your branding, dedicated support, and analytics dashboards. Contact us to learn more about partnership opportunities."
                }
              }
            ]
          })}
        </script>

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            "@id": "https://www.checkfirstcare.com/#organization",
            "name": "CheckFirstCare",
            "url": "https://www.checkfirstcare.com",
            "logo": {
              "@type": "ImageObject",
              "url": "https://www.checkfirstcare.com/og-image.jpg",
              "width": 1200,
              "height": 630
            },
            "image": "https://www.checkfirstcare.com/og-image.jpg",
            "description": "AI-powered patient triage platform providing instant healthcare guidance. HIPAA compliant clinical decision support for hospitals and healthcare organizations.",
            "founder": {
              "@type": "Person",
              "name": "Ranvir Gill",
              "jobTitle": "Founder & CEO",
              "url": "https://www.checkfirstcare.com/founder",
              "image": "https://www.checkfirstcare.com/ranvir-gill.jpg"
            },
            "foundingDate": "2024",
            "sameAs": [
              "https://www.linkedin.com/company/checkfirstcare"
            ],
            "contactPoint": {
              "@type": "ContactPoint",
              "contactType": "customer service",
              "url": "https://www.checkfirstcare.com/contact",
              "availableLanguage": "English"
            },
            "areaServed": {
              "@type": "Country",
              "name": "United States"
            },
            "knowsAbout": [
              "Patient Triage",
              "Healthcare AI",
              "Clinical Decision Support",
              "Emergency Room Triage",
              "HIPAA Compliance",
              "Hospital Triage Automation"
            ]
          })}
        </script>

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "MedicalBusiness",
            "@id": "https://www.checkfirstcare.com/#medicalbusiness",
            "name": "CheckFirstCare",
            "url": "https://www.checkfirstcare.com",
            "image": "https://www.checkfirstcare.com/og-image.jpg",
            "description": "AI-powered patient triage and clinical decision support platform for healthcare organizations. Reduce ER wait times and improve patient outcomes with intelligent symptom assessment.",
            "priceRange": "$$",
            "telephone": "+1-800-CHECKFIRST",
            "address": {
              "@type": "PostalAddress",
              "addressCountry": "US"
            },
            "geo": {
              "@type": "GeoCoordinates",
              "latitude": "37.7749",
              "longitude": "-122.4194"
            },
            "openingHoursSpecification": {
              "@type": "OpeningHoursSpecification",
              "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
              "opens": "00:00",
              "closes": "23:59"
            },
            "medicalSpecialty": "Emergency Medicine",
            "isAcceptingNewPatients": true,
            "availableService": [
              {
                "@type": "MedicalTherapy",
                "name": "AI Symptom Assessment",
                "description": "Intelligent symptom evaluation powered by clinical algorithms"
              },
              {
                "@type": "MedicalTherapy",
                "name": "Triage Recommendations",
                "description": "Evidence-based care level recommendations"
              }
            ]
          })}
        </script>

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            "name": "CheckFirstCare",
            "applicationCategory": "HealthApplication",
            "operatingSystem": "Web Browser",
            "description": "AI-powered patient triage platform for healthcare organizations. Get instant healthcare guidance with HIPAA-compliant symptom assessment.",
            "url": "https://www.checkfirstcare.com",
            "screenshot": "https://www.checkfirstcare.com/og-image.jpg",
            "author": {
              "@type": "Organization",
              "name": "CheckFirstCare"
            },
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "USD",
              "description": "Free for patients through participating healthcare organizations"
            },
            "featureList": [
              "AI-Powered Symptom Assessment",
              "Emergency Red Flag Detection",
              "Care Level Recommendations",
              "HIPAA Compliant",
              "24/7 Availability",
              "Healthcare Organization Integration"
            ],
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "4.8",
              "ratingCount": "150",
              "bestRating": "5",
              "worstRating": "1"
            }
          })}
        </script>
      </Helmet>
      <HreflangTags />
      
      <div className="min-h-screen">
        {/* Hero Section */}
      <section className="relative min-h-screen flex items-center bg-gradient-to-br from-purple-900 via-purple-800 to-teal-600">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="container mx-auto px-4 lg:px-8 relative z-10">
          <div className="max-w-4xl mx-auto text-center space-y-12">
            <div className="space-y-8">
              <div className="trust-badge mx-auto" role="status">
                <Shield className="h-4 w-4" aria-hidden="true" />
                <span>AI-Powered Healthcare Guidance</span>
              </div>
              
              <h1 className="text-5xl lg:text-7xl font-bold text-white leading-tight">
                Get Instant Healthcare 
                <span className="text-transparent bg-gradient-to-r from-blue-300 to-teal-300 bg-clip-text"> Guidance</span>
              </h1>
              
              {/* Multi-language translations */}
              <div className="flex flex-wrap justify-center gap-x-4 gap-y-2 text-white/60 text-lg">
                <span className="font-hindi">तुरंत स्वास्थ्य मार्गदर्शन प्राप्त करें</span>
                <span className="text-white/40">•</span>
                <span className="font-punjabi">ਤੁਰੰਤ ਸਿਹਤ ਮਾਰਗਦਰਸ਼ਨ ਪ੍ਰਾਪਤ ਕਰੋ</span>
                <span className="text-white/40">•</span>
                <span className="font-bengali">তাৎক্ষণিক স্বাস্থ্য নির্দেশনা পান</span>
              </div>
              
              <p className="text-xl lg:text-2xl text-white/80 leading-relaxed max-w-3xl mx-auto">
                Smart AI triage to help you decide: stay home, see your doctor, visit urgent care, or go to the ER.
              </p>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <Button variant="secondary" size="lg" asChild className="hover-lift bg-white text-purple-900 hover:bg-white/90 text-lg px-8 py-4">
                <Link to="/triage">
                  <Stethoscope className="mr-2 h-5 w-5" />
                  Start Assessment
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
              <Button 
                variant="outline" 
                size="lg" 
                onClick={() => setShowDemo(true)}
                className="hover-lift border-white/30 text-white hover:bg-white/10 text-lg px-8 py-4"
              >
                <Play className="mr-2 h-5 w-5" />
                Watch Demo
              </Button>
            </div>

            {/* HIPAA Compliance Badge */}
            <div className="pt-6">
              <Link 
                to="/hipaa-compliance"
                className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-5 py-2.5 hover:bg-white/20 transition-smooth group"
              >
                <ShieldCheck className="h-5 w-5 text-teal-300" />
                <span className="text-white font-medium text-sm">HIPAA Compliant</span>
                <span className="text-white/60 text-xs">|</span>
                <span className="text-white/70 text-xs group-hover:text-white transition-smooth">Learn More</span>
              </Link>
            </div>

            {/* Quick access note */}
            <div className="pt-4 space-y-2">
              <p className="text-white/60 text-sm">
                Need an agency code? Contact your healthcare provider
              </p>
              <p className="text-white/50 text-xs">
                Founded by{" "}
                <Link to="/founder" className="text-white/70 hover:text-white underline">
                  Ranvir Gill
                </Link>
              </p>
            </div>
          </div>
        </div>
        </section>

        {/* Trust Badges Section */}
        <section className="py-12 bg-muted/50 border-b border-border">
          <div className="container mx-auto px-4 lg:px-8">
            <p className="text-center text-muted-foreground text-sm mb-8 uppercase tracking-wider font-medium">
              Enterprise-Grade Security & Compliance
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
              {/* HIPAA Compliant */}
              <Link 
                to="/hipaa-compliance"
                className="flex flex-col items-center gap-3 p-6 rounded-xl bg-card border border-border hover:border-primary/30 hover:shadow-lg transition-all group"
              >
                <div className="p-3 rounded-full bg-primary/10 group-hover:bg-primary/20 transition-colors">
                  <ShieldCheck className="h-6 w-6 text-primary" />
                </div>
                <div className="text-center">
                  <p className="font-semibold text-foreground text-sm">HIPAA</p>
                  <p className="text-muted-foreground text-xs">Compliant</p>
                </div>
              </Link>

              {/* SOC 2 Type II */}
              <div className="flex flex-col items-center gap-3 p-6 rounded-xl bg-card border border-border">
                <div className="p-3 rounded-full bg-primary/10">
                  <FileCheck className="h-6 w-6 text-primary" />
                </div>
                <div className="text-center">
                  <p className="font-semibold text-foreground text-sm">SOC 2</p>
                  <p className="text-muted-foreground text-xs">Type II Certified</p>
                </div>
              </div>

              {/* AES-256 Encryption */}
              <div className="flex flex-col items-center gap-3 p-6 rounded-xl bg-card border border-border">
                <div className="p-3 rounded-full bg-primary/10">
                  <Lock className="h-6 w-6 text-primary" />
                </div>
                <div className="text-center">
                  <p className="font-semibold text-foreground text-sm">AES-256</p>
                  <p className="text-muted-foreground text-xs">Encryption</p>
                </div>
              </div>

              {/* Secure Infrastructure */}
              <div className="flex flex-col items-center gap-3 p-6 rounded-xl bg-card border border-border">
                <div className="p-3 rounded-full bg-primary/10">
                  <KeyRound className="h-6 w-6 text-primary" />
                </div>
                <div className="text-center">
                  <p className="font-semibold text-foreground text-sm">MFA</p>
                  <p className="text-muted-foreground text-xs">Protected Access</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-20 bg-background">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                Smart Healthcare Decisions Made Simple
              </h2>
              
              {/* Multi-language translations */}
              <div className="flex flex-wrap justify-center gap-x-3 gap-y-2 text-muted-foreground text-lg mb-4">
                <span className="font-hindi">स्मार्ट स्वास्थ्य निर्णय आसान बनाए</span>
                <span className="text-muted-foreground/50">•</span>
                <span className="font-punjabi">ਸਮਾਰਟ ਸਿਹਤ ਫੈਸਲੇ ਆਸਾਨ ਬਣਾਏ</span>
                <span className="text-muted-foreground/50">•</span>
                <span className="font-bengali">স্মার্ট স্বাস্থ্য সিদ্ধান্ত সহজ করা হয়েছে</span>
              </div>
              <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
                Our AI-powered triage platform helps patients get the right care at the right time, 
                reducing unnecessary ER visits and improving outcomes.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
              {/* AI-Powered Triage */}
              <div className="group p-8 rounded-2xl bg-card border border-border hover:border-primary/30 hover:shadow-xl transition-all">
                <div className="p-4 rounded-xl bg-gradient-to-br from-primary/20 to-primary/5 w-fit mb-6 group-hover:scale-110 transition-transform">
                  <Brain className="h-8 w-8 text-primary" />
                </div>
                <h3 className="text-xl font-semibold text-foreground mb-3">AI-Powered Triage</h3>
                <p className="text-muted-foreground leading-relaxed">
                  Advanced algorithms analyze symptoms and medical history to provide personalized care recommendations in seconds.
                </p>
              </div>

              {/* Instant Recommendations */}
              <div className="group p-8 rounded-2xl bg-card border border-border hover:border-primary/30 hover:shadow-xl transition-all">
                <div className="p-4 rounded-xl bg-gradient-to-br from-primary/20 to-primary/5 w-fit mb-6 group-hover:scale-110 transition-transform">
                  <Clock className="h-8 w-8 text-primary" />
                </div>
                <h3 className="text-xl font-semibold text-foreground mb-3">Instant Recommendations</h3>
                <p className="text-muted-foreground leading-relaxed">
                  Get immediate guidance on whether to stay home, visit your doctor, go to urgent care, or head to the ER.
                </p>
              </div>

              {/* Patient-Centered */}
              <div className="group p-8 rounded-2xl bg-card border border-border hover:border-primary/30 hover:shadow-xl transition-all">
                <div className="p-4 rounded-xl bg-gradient-to-br from-primary/20 to-primary/5 w-fit mb-6 group-hover:scale-110 transition-transform">
                  <HeartPulse className="h-8 w-8 text-primary" />
                </div>
                <h3 className="text-xl font-semibold text-foreground mb-3">Patient-Centered Care</h3>
                <p className="text-muted-foreground leading-relaxed">
                  Designed with patients in mind, our intuitive interface guides you through symptom assessment with ease.
                </p>
              </div>

              {/* Red Flag Detection */}
              <div className="group p-8 rounded-2xl bg-card border border-border hover:border-primary/30 hover:shadow-xl transition-all">
                <div className="p-4 rounded-xl bg-gradient-to-br from-primary/20 to-primary/5 w-fit mb-6 group-hover:scale-110 transition-transform">
                  <Activity className="h-8 w-8 text-primary" />
                </div>
                <h3 className="text-xl font-semibold text-foreground mb-3">Emergency Detection</h3>
                <p className="text-muted-foreground leading-relaxed">
                  Automatically identifies critical symptoms and red flags, ensuring urgent cases receive immediate attention.
                </p>
              </div>

              {/* For Healthcare Organizations */}
              <div className="group p-8 rounded-2xl bg-card border border-border hover:border-primary/30 hover:shadow-xl transition-all">
                <div className="p-4 rounded-xl bg-gradient-to-br from-primary/20 to-primary/5 w-fit mb-6 group-hover:scale-110 transition-transform">
                  <Building2 className="h-8 w-8 text-primary" />
                </div>
                <h3 className="text-xl font-semibold text-foreground mb-3">Healthcare Integration</h3>
                <p className="text-muted-foreground leading-relaxed">
                  Seamlessly integrates with healthcare organizations to streamline patient intake and reduce wait times.
                </p>
              </div>

              {/* Better Outcomes */}
              <div className="group p-8 rounded-2xl bg-card border border-border hover:border-primary/30 hover:shadow-xl transition-all">
                <div className="p-4 rounded-xl bg-gradient-to-br from-primary/20 to-primary/5 w-fit mb-6 group-hover:scale-110 transition-transform">
                  <Users className="h-8 w-8 text-primary" />
                </div>
                <h3 className="text-xl font-semibold text-foreground mb-3">Better Outcomes</h3>
                <p className="text-muted-foreground leading-relaxed">
                  Help patients make informed decisions, reducing unnecessary ER visits by 39% while ensuring critical cases get priority care.
                </p>
              </div>
            </div>

            {/* CTA */}
            <div className="text-center mt-16">
              <Button size="lg" asChild className="hover-lift">
                <Link to="/triage">
                  Start Your Assessment
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
            </div>
          </div>
        </section>

        {/* How It Works Section */}
        <section className="py-20 bg-muted/30">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="text-center mb-16">
              <div className="inline-flex items-center gap-2 bg-primary/10 rounded-full px-4 py-2 mb-6">
                <Stethoscope className="h-4 w-4 text-primary" />
                <span className="text-primary text-sm font-medium">Simple Process</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                How It Works
              </h2>
              
              {/* Multi-language translations */}
              <div className="flex flex-wrap justify-center gap-x-3 gap-y-2 text-muted-foreground text-lg mb-4">
                <span className="font-hindi">यह कैसे काम करता है</span>
                <span className="text-muted-foreground/50">•</span>
                <span className="font-punjabi">ਇਹ ਕਿਵੇਂ ਕੰਮ ਕਰਦਾ ਹੈ</span>
                <span className="text-muted-foreground/50">•</span>
                <span className="font-bengali">এটি কিভাবে কাজ করে</span>
              </div>
              <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
                Get personalized healthcare guidance in just a few simple steps
              </p>
            </div>

            <div className="max-w-5xl mx-auto">
              {/* Steps */}
              <div className="relative">
                {/* Connection Line - Desktop */}
                <div className="hidden lg:block absolute top-24 left-[10%] right-[10%] h-0.5 bg-gradient-to-r from-primary/20 via-primary to-primary/20" />
                
                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-6">
                  {/* Step 1 */}
                  <div className="relative group">
                    <div className="flex flex-col items-center text-center">
                      <div className="relative z-10 w-16 h-16 rounded-full bg-gradient-to-br from-primary to-primary/80 flex items-center justify-center text-primary-foreground text-2xl font-bold shadow-lg group-hover:scale-110 transition-transform mb-6">
                        1
                      </div>
                      <div className="p-6 rounded-2xl bg-card border border-border group-hover:border-primary/30 group-hover:shadow-lg transition-all w-full">
                        <div className="p-3 rounded-xl bg-primary/10 w-fit mx-auto mb-4">
                          <KeyRound className="h-6 w-6 text-primary" />
                        </div>
                        <h3 className="text-lg font-semibold text-foreground mb-2">Sign In</h3>
                        <p className="text-muted-foreground text-sm leading-relaxed">
                          Enter your email and verify with a secure one-time code sent to your inbox
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Step 2 */}
                  <div className="relative group">
                    <div className="flex flex-col items-center text-center">
                      <div className="relative z-10 w-16 h-16 rounded-full bg-gradient-to-br from-primary to-primary/80 flex items-center justify-center text-primary-foreground text-2xl font-bold shadow-lg group-hover:scale-110 transition-transform mb-6">
                        2
                      </div>
                      <div className="p-6 rounded-2xl bg-card border border-border group-hover:border-primary/30 group-hover:shadow-lg transition-all w-full">
                        <div className="p-3 rounded-xl bg-primary/10 w-fit mx-auto mb-4">
                          <FileCheck className="h-6 w-6 text-primary" />
                        </div>
                        <h3 className="text-lg font-semibold text-foreground mb-2">Describe Symptoms</h3>
                        <p className="text-muted-foreground text-sm leading-relaxed">
                          Answer simple questions about your symptoms, their severity, and duration
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Step 3 */}
                  <div className="relative group">
                    <div className="flex flex-col items-center text-center">
                      <div className="relative z-10 w-16 h-16 rounded-full bg-gradient-to-br from-primary to-primary/80 flex items-center justify-center text-primary-foreground text-2xl font-bold shadow-lg group-hover:scale-110 transition-transform mb-6">
                        3
                      </div>
                      <div className="p-6 rounded-2xl bg-card border border-border group-hover:border-primary/30 group-hover:shadow-lg transition-all w-full">
                        <div className="p-3 rounded-xl bg-primary/10 w-fit mx-auto mb-4">
                          <Brain className="h-6 w-6 text-primary" />
                        </div>
                        <h3 className="text-lg font-semibold text-foreground mb-2">AI Analysis</h3>
                        <p className="text-muted-foreground text-sm leading-relaxed">
                          Our intelligent system analyzes your responses using clinical protocols
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Step 4 */}
                  <div className="relative group">
                    <div className="flex flex-col items-center text-center">
                      <div className="relative z-10 w-16 h-16 rounded-full bg-gradient-to-br from-teal-500 to-teal-600 flex items-center justify-center text-white text-2xl font-bold shadow-lg group-hover:scale-110 transition-transform mb-6">
                        ✓
                      </div>
                      <div className="p-6 rounded-2xl bg-card border border-border group-hover:border-teal-500/30 group-hover:shadow-lg transition-all w-full">
                        <div className="p-3 rounded-xl bg-teal-500/10 w-fit mx-auto mb-4">
                          <HeartPulse className="h-6 w-6 text-teal-600" />
                        </div>
                        <h3 className="text-lg font-semibold text-foreground mb-2">Get Guidance</h3>
                        <p className="text-muted-foreground text-sm leading-relaxed">
                          Receive personalized recommendations: home care, doctor visit, urgent care, or ER
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* CTA */}
              <div className="text-center mt-16">
                <Button size="lg" asChild className="hover-lift">
                  <Link to="/triage">
                    Start Your Assessment Now
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                </Button>
                <p className="text-muted-foreground text-sm mt-4">
                  Takes less than 5 minutes to complete
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Testimonials Section */}
        <section className="py-20 bg-muted/30">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                Trusted by Healthcare Organizations
              </h2>
              
              {/* Multi-language translations */}
              <div className="flex flex-wrap justify-center gap-x-3 gap-y-2 text-muted-foreground text-lg mb-4">
                <span className="font-hindi">स्वास्थ्य संगठनों द्वारा विश्वसनीय</span>
                <span className="text-muted-foreground/50">•</span>
                <span className="font-punjabi">ਸਿਹਤ ਸੰਸਥਾਵਾਂ ਦੁਆਰਾ ਭਰੋਸੇਮੰਦ</span>
                <span className="text-muted-foreground/50">•</span>
                <span className="font-bengali">স্বাস্থ্য সংস্থাগুলির দ্বারা বিশ্বস্ত</span>
              </div>
              <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
                See what healthcare providers and patients are saying about CheckFirstCare
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
              {/* Healthcare Organization Testimonial */}
              <div className="relative p-8 rounded-2xl bg-card border border-border shadow-sm">
                <Quote className="absolute top-6 right-6 h-8 w-8 text-primary/20" />
                <div className="mb-6">
                  <p className="text-foreground leading-relaxed italic">
                    "CheckFirstCare has significantly reduced our costs by cutting down on 
                    unnecessary ER visits and eliminating false alarms. We're saving thousands 
                    each month while providing better care."
                  </p>
                </div>
                <a 
                  href="https://carefirsthomehealth.org" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center gap-4 group"
                >
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                    <Building2 className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <p className="font-semibold text-foreground group-hover:text-primary transition-colors">Care First Home Health</p>
                    <p className="text-muted-foreground text-sm">Home Healthcare Provider</p>
                  </div>
                </a>
              </div>

              {/* Patient Testimonial 1 */}
              <div className="relative p-8 rounded-2xl bg-card border border-border shadow-sm">
                <Quote className="absolute top-6 right-6 h-8 w-8 text-primary/20" />
                <div className="mb-6">
                  <p className="text-foreground leading-relaxed italic">
                    "I was unsure if my symptoms warranted an ER visit. CheckFirstCare guided me 
                    through a quick assessment and recommended urgent care instead. It saved me 
                    hours of waiting and hundreds in medical bills."
                  </p>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-teal-500 to-teal-600 flex items-center justify-center text-white font-semibold">
                    SM
                  </div>
                  <div>
                    <p className="font-semibold text-foreground">Sarah M.</p>
                    <p className="text-muted-foreground text-sm">Patient, Philadelphia</p>
                  </div>
                </div>
              </div>

              {/* Patient Testimonial 2 */}
              <div className="relative p-8 rounded-2xl bg-card border border-border shadow-sm">
                <Quote className="absolute top-6 right-6 h-8 w-8 text-primary/20" />
                <div className="mb-6">
                  <p className="text-foreground leading-relaxed italic">
                    "As a parent, having instant access to reliable health guidance is invaluable. 
                    When my daughter had a high fever at midnight, CheckFirstCare helped me make 
                    the right call quickly."
                  </p>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center text-white font-semibold">
                    JT
                  </div>
                  <div>
                    <p className="font-semibold text-foreground">James T.</p>
                    <p className="text-muted-foreground text-sm">Parent, New Jersey</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Partner CTA */}
            <div className="mt-16 text-center">
              <p className="text-muted-foreground mb-4">
                Want to bring CheckFirstCare to your healthcare organization?
              </p>
              <Button variant="outline" size="lg" asChild>
                <Link to="/contact">
                  Partner With Us
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20 bg-background">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="text-center mb-16">
              <div className="inline-flex items-center gap-2 bg-primary/10 rounded-full px-4 py-2 mb-6">
                <HelpCircle className="h-4 w-4 text-primary" />
                <span className="text-primary text-sm font-medium">FAQ</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                Frequently Asked Questions
              </h2>
              <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
                Everything you need to know about CheckFirstCare's triage platform
              </p>
            </div>

            <div className="max-w-3xl mx-auto">
              <Accordion type="single" collapsible className="space-y-4">
                <AccordionItem value="item-1" className="bg-card border border-border rounded-xl px-6">
                  <AccordionTrigger className="text-left font-semibold text-foreground hover:no-underline py-6">
                    What is CheckFirstCare and how does it work?
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground pb-6">
                    CheckFirstCare is an AI-powered patient triage platform that helps you make informed healthcare decisions. 
                    Simply answer questions about your symptoms, and our intelligent system analyzes your responses to recommend 
                    the most appropriate level of care—whether that's staying home with self-care advice, scheduling a doctor's 
                    visit, going to urgent care, or heading to the emergency room.
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-2" className="bg-card border border-border rounded-xl px-6">
                  <AccordionTrigger className="text-left font-semibold text-foreground hover:no-underline py-6">
                    Is my health information secure and private?
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground pb-6">
                    Absolutely. CheckFirstCare is fully HIPAA compliant and uses enterprise-grade security measures including 
                    AES-256 encryption, SOC 2 Type II certification, and multi-factor authentication. Your health data is 
                    encrypted both in transit and at rest, and we never sell or share your personal information with third parties.
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-3" className="bg-card border border-border rounded-xl px-6">
                  <AccordionTrigger className="text-left font-semibold text-foreground hover:no-underline py-6">
                    Does CheckFirstCare replace medical advice from a doctor?
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground pb-6">
                    No, CheckFirstCare is designed to be a decision support tool, not a replacement for professional medical 
                    advice. Our platform helps you determine the urgency of your symptoms and guides you to the appropriate 
                    level of care. For any medical emergency, always call 911 or go directly to the emergency room.
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-4" className="bg-card border border-border rounded-xl px-6">
                  <AccordionTrigger className="text-left font-semibold text-foreground hover:no-underline py-6">
                    How accurate is the AI triage system?
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground pb-6">
                    Our AI triage system is built on evidence-based clinical protocols and continuously refined with input 
                    from healthcare professionals. The system is designed to err on the side of caution—when in doubt, it 
                    recommends seeking higher levels of care. Our algorithms have been validated against established 
                    triage standards used in emergency departments.
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-5" className="bg-card border border-border rounded-xl px-6">
                  <AccordionTrigger className="text-left font-semibold text-foreground hover:no-underline py-6">
                    What do I need to get started with CheckFirstCare?
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground pb-6">
                    To use CheckFirstCare, you'll need an agency code from your healthcare provider. This connects you to 
                    your healthcare organization's account and ensures your triage results are properly documented. Simply 
                    enter your email, verify with the one-time code, and input your agency code to begin your assessment.
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-6" className="bg-card border border-border rounded-xl px-6">
                  <AccordionTrigger className="text-left font-semibold text-foreground hover:no-underline py-6">
                    How can my healthcare organization partner with CheckFirstCare?
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground pb-6">
                    Healthcare organizations can integrate CheckFirstCare to streamline patient intake, reduce unnecessary 
                    ER visits, and improve care coordination. We offer customizable solutions with your branding, dedicated 
                    support, and analytics dashboards.{" "}
                    <Link to="/contact" className="text-primary hover:underline font-medium">
                      Contact us
                    </Link>{" "}
                    to learn more about partnership opportunities.
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>

            {/* Still have questions CTA */}
            <div className="text-center mt-12">
              <p className="text-muted-foreground mb-4">
                Still have questions? We're here to help.
              </p>
              <Button variant="outline" size="lg" asChild>
                <Link to="/contact">
                  Contact Support
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </div>
          </div>
        </section>

      </div>

      {/* Platform Demo Modal */}
      <PlatformDemoModal open={showDemo} onClose={() => setShowDemo(false)} />
    </>
  );
}